import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { User, Role, Patient, Encounter } from '../types';
import { UsersIcon, Trash2Icon, UserPlusIcon, PrinterIcon, SearchIcon, ActivityIcon, FileTextIcon, EyeIcon, EyeOffIcon, EditIcon, ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon } from 'lucide-react';
type Tab = 'overview' | 'patients' | 'staff';
export const AdminDashboard: React.FC = () => {
  const {
    users,
    patients,
    addUser,
    updateUser,
    deleteUser
  } = useData();
  // State
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [expandedEncounters, setExpandedEncounters] = useState<Record<string, boolean>>({});
  // User Modal State
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState<Role>('Clinician');
  const [userPassword, setUserPassword] = useState('');
  const [userError, setUserError] = useState('');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  // Computed Stats
  const allEncounters = patients.flatMap(p => p.encounters);
  const activeEncounters = allEncounters.filter(e => e.status !== 'Completed');
  const statusCounts = allEncounters.reduce((acc, curr) => {
    acc[curr.status] = (acc[curr.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const filteredPatients = patients.filter(p => p.personalDetails.fullNames.toLowerCase().includes(patientSearch.toLowerCase()) || p.personalDetails.fileNo.toLowerCase().includes(patientSearch.toLowerCase()));
  // Handlers
  const toggleEncounter = (id: string) => {
    setExpandedEncounters(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const togglePasswordVisibility = (id: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const handlePrint = () => {
    window.print();
  };
  const openUserModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserName(user.name);
      setUserRole(user.role);
      setUserPassword(user.password || '');
    } else {
      setEditingUser(null);
      setUserName('');
      setUserRole('Clinician');
      setUserPassword('');
    }
    setUserError('');
    setShowUserModal(true);
  };
  const validatePassword = (pwd: string) => {
    if (pwd.length < 6) return 'Password must be at least 6 characters long';
    if (!/\d/.test(pwd)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return 'Password must contain at least one special character';
    return '';
  };
  const handleSaveUser = (e: React.FormEvent) => {
    e.preventDefault();
    const pwdError = validatePassword(userPassword);
    if (pwdError) {
      setUserError(pwdError);
      return;
    }
    if (editingUser) {
      updateUser({
        ...editingUser,
        name: userName,
        role: userRole,
        password: userPassword
      });
    } else {
      addUser({
        id: Date.now().toString(),
        name: userName,
        role: userRole,
        password: userPassword
      });
    }
    setShowUserModal(false);
  };
  const roles: Role[] = ['Administrator', 'Reception', 'Nursing', 'Clinician', 'Laboratory', 'Radiology', 'Pharmacy'];
  // Render Helpers
  const renderOverview = () => <div className="space-y-6" data-id="element-77">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-78">
        <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100" data-id="element-79">
          <div className="p-5" data-id="element-80">
            <div className="flex items-center" data-id="element-81">
              <div className="flex-shrink-0" data-id="element-82">
                <UsersIcon className="h-6 w-6 text-teal-600" data-id="element-83" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-84">
                <dl data-id="element-85">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-86">
                    Total Staff
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-87">
                    {users.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100" data-id="element-88">
          <div className="p-5" data-id="element-89">
            <div className="flex items-center" data-id="element-90">
              <div className="flex-shrink-0" data-id="element-91">
                <UsersIcon className="h-6 w-6 text-blue-600" data-id="element-92" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-93">
                <dl data-id="element-94">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-95">
                    Total Patients
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-96">
                    {patients.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100" data-id="element-97">
          <div className="p-5" data-id="element-98">
            <div className="flex items-center" data-id="element-99">
              <div className="flex-shrink-0" data-id="element-100">
                <ActivityIcon className="h-6 w-6 text-orange-600" data-id="element-101" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-102">
                <dl data-id="element-103">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-104">
                    Active Encounters
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900" data-id="element-105">
                    {activeEncounters.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border border-gray-100 p-6" data-id="element-106">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-107">
          Encounter Status Breakdown
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-108">
          {Object.entries(statusCounts).map(([status, count]) => <div key={status} className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-109">
              <div className="text-sm text-gray-500 mb-1" data-id="element-110">{status}</div>
              <div className="text-2xl font-bold text-teal-700" data-id="element-111">{count}</div>
            </div>)}
        </div>
      </div>
    </div>;
  const renderPatientDetail = (patient: Patient) => <div className="bg-white shadow rounded-lg border border-gray-100 p-6" data-id="element-112">
      <div className="flex justify-between items-center mb-6 border-b pb-4" data-id="element-113">
        <button onClick={() => setSelectedPatient(null)} className="inline-flex items-center text-sm text-gray-500 hover:text-teal-600" data-id="element-114">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-115" /> Back to Patient List
        </button>
        <button onClick={handlePrint} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-116">
          <PrinterIcon className="h-4 w-4 mr-2" data-id="element-117" /> Print File
        </button>
      </div>

      {/* Personal Details */}
      <div className="mb-8" data-id="element-118">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-119">
          Patient Information
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200 text-sm" data-id="element-120">
          <div data-id="element-121">
            <span className="text-gray-500 block" data-id="element-122">Full Name</span>
            <span className="font-medium" data-id="element-123">
              {patient.personalDetails.fullNames}
            </span>
          </div>
          <div data-id="element-124">
            <span className="text-gray-500 block" data-id="element-125">File No</span>
            <span className="font-medium" data-id="element-126">
              {patient.personalDetails.fileNo}
            </span>
          </div>
          <div data-id="element-127">
            <span className="text-gray-500 block" data-id="element-128">Sex / Age</span>
            <span className="font-medium" data-id="element-129">
              {patient.personalDetails.sex}, {patient.personalDetails.age}
            </span>
          </div>
          <div data-id="element-130">
            <span className="text-gray-500 block" data-id="element-131">Contact</span>
            <span className="font-medium" data-id="element-132">
              {patient.personalDetails.contact}
            </span>
          </div>
          <div className="md:col-span-2" data-id="element-133">
            <span className="text-gray-500 block" data-id="element-134">Address</span>
            <span className="font-medium" data-id="element-135">
              {patient.personalDetails.homeAddress}
            </span>
          </div>
          <div data-id="element-136">
            <span className="text-gray-500 block" data-id="element-137">Allergies</span>
            <span className="font-bold text-red-600" data-id="element-138">
              {patient.personalDetails.allergies || 'None'}
            </span>
          </div>
          <div data-id="element-139">
            <span className="text-gray-500 block" data-id="element-140">HIV Status</span>
            <span className="font-medium" data-id="element-141">
              {patient.personalDetails.hivStatus}
            </span>
          </div>
        </div>
      </div>

      {/* Encounters */}
      <div data-id="element-142">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-143">
          Encounter History ({patient.encounters.length})
        </h3>
        <div className="space-y-4" data-id="element-144">
          {patient.encounters.slice().reverse().map((enc, idx) => {
          const isExpanded = expandedEncounters[enc.id];
          const hasLabResults = enc.investigations.labResults && Object.keys(enc.investigations.labResults).length > 0;
          const hasRadiology = enc.investigations.radiologyReport || enc.investigations.radiologyImages?.length > 0;
          return <div key={enc.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-145">
                  <div className="bg-gray-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-100" onClick={() => toggleEncounter(enc.id)} data-id="element-146">
                    <div className="flex items-center space-x-4" data-id="element-147">
                      <span className="font-semibold text-teal-800" data-id="element-148">
                        {new Date(enc.date).toLocaleDateString()}
                      </span>
                      <span className="text-sm text-gray-500" data-id="element-149">
                        Dr. {enc.doctorName || 'Unknown'}
                      </span>
                      <span className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-medium text-gray-600" data-id="element-150">
                        {enc.status}
                      </span>
                    </div>
                    {isExpanded ? <ChevronUpIcon className="h-5 w-5 text-gray-400" data-id="element-151" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" data-id="element-152" />}
                  </div>

                  {isExpanded && <div className="p-4 bg-white space-y-6" data-id="element-153">
                      {/* Vitals */}
                      {enc.vitals.length > 0 && <div data-id="element-154">
                          <h4 className="text-sm font-bold text-gray-700 mb-2 border-b pb-1" data-id="element-155">
                            Vitals (Latest)
                          </h4>
                          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 text-xs" data-id="element-156">
                            {Object.entries(enc.vitals[enc.vitals.length - 1]).map(([k, v]) => <div key={k} className="bg-gray-50 p-1.5 rounded border border-gray-100" data-id="element-157">
                                <span className="text-gray-500 block capitalize" data-id="element-158">
                                  {k}
                                </span>
                                <span className="font-medium" data-id="element-159">{v || '-'}</span>
                              </div>)}
                          </div>
                        </div>}

                      {/* Clinical Notes */}
                      <div data-id="element-160">
                        <h4 className="text-sm font-bold text-gray-700 mb-2 border-b pb-1" data-id="element-161">
                          Clinical Notes
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-162">
                          <div data-id="element-163">
                            <span className="text-gray-500 block mb-1" data-id="element-164">
                              Presenting Complaint
                            </span>
                            <p className="bg-gray-50 p-2 rounded border border-gray-100 whitespace-pre-wrap" data-id="element-165">
                              {enc.clinicalNotes.presentingComplaint || 'None recorded'}
                            </p>
                          </div>
                          <div data-id="element-166">
                            <span className="text-gray-500 block mb-1" data-id="element-167">
                              Working Diagnosis
                            </span>
                            <ul className="bg-gray-50 p-2 rounded border border-gray-100 list-disc list-inside" data-id="element-168">
                              {enc.clinicalNotes.workingDiagnosis.length > 0 ? enc.clinicalNotes.workingDiagnosis.map((d, i) => <li key={i} data-id="element-169">{d}</li>) : <li data-id="element-170">None recorded</li>}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Lab Results */}
                      {hasLabResults && <div data-id="element-171">
                          <h4 className="text-sm font-bold text-teal-700 mb-2 border-b border-teal-100 pb-1" data-id="element-172">
                            Laboratory Results
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-sm" data-id="element-173">
                            {Object.entries(enc.investigations.labResults).map(([key, value]) => <div key={key} className="bg-teal-50 p-2 rounded border border-teal-100" data-id="element-174">
                                  <span className="text-teal-600 block text-xs capitalize" data-id="element-175">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                  </span>
                                  <span className="font-medium text-gray-900" data-id="element-176">
                                    {value}
                                  </span>
                                </div>)}
                          </div>
                        </div>}

                      {/* Radiology Results */}
                      {hasRadiology && <div data-id="element-177">
                          <h4 className="text-sm font-bold text-purple-700 mb-2 border-b border-purple-100 pb-1" data-id="element-178">
                            Radiology: {enc.investigations.scanType || 'Scan'}
                          </h4>
                          <div className="bg-purple-50 p-3 rounded border border-purple-100 text-sm" data-id="element-179">
                            <p className="whitespace-pre-wrap mb-3" data-id="element-180">
                              {enc.investigations.radiologyReport || 'No report text.'}
                            </p>
                            {enc.investigations.radiographerName && <p className="text-xs text-purple-600 italic mb-3" data-id="element-181">
                                - Reported by{' '}
                                {enc.investigations.radiographerName}
                              </p>}
                            {enc.investigations.radiologyImages?.length > 0 && <div className="flex gap-2 overflow-x-auto" data-id="element-182">
                                {enc.investigations.radiologyImages.map((img, i) => <img key={i} src={img} alt="Scan" className="h-20 w-20 object-cover rounded border border-purple-200" data-id="element-183" />)}
                              </div>}
                          </div>
                        </div>}

                      {/* Follow-ups */}
                      {enc.followUpDates.length > 0 && <div data-id="element-184">
                          <h4 className="text-sm font-bold text-amber-700 mb-2 border-b border-amber-100 pb-1" data-id="element-185">
                            Review Dates
                          </h4>
                          <div className="flex gap-2" data-id="element-186">
                            {enc.followUpDates.map((date, i) => <span key={i} className="px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded text-sm" data-id="element-187">
                                {new Date(date).toLocaleDateString()}
                              </span>)}
                          </div>
                        </div>}
                    </div>}
                </div>;
        })}
        </div>
      </div>
    </div>;
  const renderPatientsList = () => <div className="bg-white shadow rounded-lg border border-gray-100" data-id="element-188">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-189">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-190">
          Patient Directory
        </h3>
        <div className="relative w-64" data-id="element-191">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-192">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-193" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" placeholder="Search patients..." value={patientSearch} onChange={e => setPatientSearch(e.target.value)} data-id="element-194" />
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-195">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-196">
          <thead className="bg-gray-50" data-id="element-197">
            <tr data-id="element-198">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-199">
                File No
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-200">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-201">
                Encounters
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-202">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-203">
            {filteredPatients.map(p => <tr key={p.id} className="hover:bg-gray-50" data-id="element-204">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-205">
                  {p.personalDetails.fileNo}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-206">
                  {p.personalDetails.fullNames}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-207">
                  {p.encounters.length} visits
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-208">
                  <button onClick={() => setSelectedPatient(p)} className="text-teal-600 hover:text-teal-900 inline-flex items-center" data-id="element-209">
                    <FileTextIcon className="h-4 w-4 mr-1" data-id="element-210" /> View File
                  </button>
                </td>
              </tr>)}
            {filteredPatients.length === 0 && <tr data-id="element-211">
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500 text-sm" data-id="element-212">
                  No patients found.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
  const renderStaffManagement = () => <div className="bg-white shadow rounded-lg border border-gray-100" data-id="element-213">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-214">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-215">
          Staff Management
        </h3>
        <button onClick={() => openUserModal()} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none" data-id="element-216">
          <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" data-id="element-217" />
          Add User
        </button>
      </div>
      <div className="overflow-x-auto" data-id="element-218">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-219">
          <thead className="bg-gray-50" data-id="element-220">
            <tr data-id="element-221">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-222">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-223">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-224">
                Password
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-225">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-226">
            {users.map(u => <tr key={u.id} className="hover:bg-gray-50" data-id="element-227">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-228">
                  {u.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-229">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-100 text-teal-800" data-id="element-230">
                    {u.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-231">
                  <div className="flex items-center space-x-2" data-id="element-232">
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs" data-id="element-233">
                      {showPasswords[u.id] ? u.password : '••••••••'}
                    </span>
                    <button onClick={() => togglePasswordVisibility(u.id)} className="text-gray-400 hover:text-gray-600" data-id="element-234">
                      {showPasswords[u.id] ? <EyeOffIcon className="h-4 w-4" data-id="element-235" /> : <EyeIcon className="h-4 w-4" data-id="element-236" />}
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3" data-id="element-237">
                  <button onClick={() => openUserModal(u)} className="text-teal-600 hover:text-teal-900" data-id="element-238">
                    <EditIcon className="h-5 w-5 inline" data-id="element-239" />
                  </button>
                  {u.role !== 'Administrator' && <button onClick={() => deleteUser(u.id)} className="text-red-600 hover:text-red-900" data-id="element-240">
                      <Trash2Icon className="h-5 w-5 inline" data-id="element-241" />
                    </button>}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
  return <div className="space-y-6" data-id="element-242">
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-243">
        <nav className="-mb-px flex space-x-8" data-id="element-244">
          {(['overview', 'patients', 'staff'] as Tab[]).map(tab => <button key={tab} onClick={() => {
          setActiveTab(tab);
          if (tab !== 'patients') setSelectedPatient(null);
        }} className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize
                ${activeTab === tab ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `} data-id="element-245">
              {tab === 'staff' ? 'Staff Management' : tab === 'patients' ? 'Patient Files' : 'System Overview'}
            </button>)}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'patients' && (selectedPatient ? renderPatientDetail(selectedPatient) : renderPatientsList())}
      {activeTab === 'staff' && renderStaffManagement()}

      {/* User Modal */}
      {showUserModal && <div className="fixed z-50 inset-0 overflow-y-auto" data-id="element-246">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-247">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowUserModal(false)} data-id="element-248"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-249">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-250">
              <form onSubmit={handleSaveUser} data-id="element-251">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-252">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-253">
                    {editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}
                  </h3>

                  {userError && <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded text-sm" data-id="element-254">
                      {userError}
                    </div>}

                  <div className="space-y-4" data-id="element-255">
                    <div data-id="element-256">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-257">
                        Full Name
                      </label>
                      <input type="text" required value={userName} onChange={e => setUserName(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-258" />
                    </div>

                    <div data-id="element-259">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-260">
                        Role
                      </label>
                      <select value={userRole} onChange={e => setUserRole(e.target.value as Role)} disabled={editingUser?.role === 'Administrator'} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm disabled:bg-gray-100" data-id="element-261">
                        {roles.map(r => <option key={r} value={r} data-id="element-262">
                            {r}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-263">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-264">
                        Password
                      </label>
                      <input type="text" required value={userPassword} onChange={e => setUserPassword(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-265" />
                      <p className="mt-1 text-xs text-gray-500" data-id="element-266">
                        Min 6 chars, must include number and special character.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-267">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm" data-id="element-268">
                    Save
                  </button>
                  <button type="button" onClick={() => setShowUserModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-269">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}
    </div>;
};